import { useRef, type PointerEvent as ReactPointerEvent } from 'react';
import { cn } from '@/lib/utils';

const DISMISS_DISTANCE = 90;
const DISMISS_VELOCITY = 0.6; // px per ms

/**
 * The little bar at the top of a phone bottom sheet. Dragging it down slides
 * the sheet with the finger and dismisses it past a threshold, like iOS.
 *
 * Dismissal goes through the dialog's own Escape handling, so a dialog that
 * refuses Escape (onEscapeKeyDown preventDefault) also refuses the swipe.
 */
export function SheetGrabber({ className }: { className?: string }) {
	const ref = useRef<HTMLDivElement>(null);

	const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
		const panel = ref.current?.closest<HTMLElement>('[role="dialog"], [role="alertdialog"]');
		if (!panel || event.button !== 0) return;

		const startY = event.clientY;
		const startTime = performance.now();
		let dy = 0;
		event.currentTarget.setPointerCapture(event.pointerId);
		panel.style.transition = 'none';

		const onMove = (e: PointerEvent) => {
			dy = Math.max(0, e.clientY - startY);
			panel.style.transform = `translateY(${dy}px)`;
		};

		const settle = () => {
			panel.style.transition = 'transform 220ms cubic-bezier(0.32, 0.72, 0, 1)';
			panel.style.transform = '';
			window.setTimeout(() => {
				panel.style.transition = '';
			}, 240);
		};

		const onUp = () => {
			window.removeEventListener('pointermove', onMove);
			window.removeEventListener('pointerup', onUp);
			window.removeEventListener('pointercancel', onUp);
			const velocity = dy / Math.max(1, performance.now() - startTime);
			if (dy > DISMISS_DISTANCE || (dy > 24 && velocity > DISMISS_VELOCITY)) {
				panel.style.transition = '';
				panel.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true, cancelable: true }));
				// Still open: the dialog ignores Escape. Spring back.
				window.setTimeout(() => {
					if (panel.isConnected && panel.getAttribute('data-state') === 'open') settle();
				}, 30);
			} else {
				settle();
			}
		};

		window.addEventListener('pointermove', onMove);
		window.addEventListener('pointerup', onUp);
		window.addEventListener('pointercancel', onUp);
	};

	return (
		<div
			ref={ref}
			aria-hidden
			onPointerDown={onPointerDown}
			className={cn(
				'absolute left-1/2 top-0 z-10 flex h-6 w-28 -translate-x-1/2 touch-none justify-center pt-2 md:hidden',
				className,
			)}
		>
			<div className="h-1.5 w-10 rounded-full bg-muted-foreground/30" />
		</div>
	);
}
